import React, { useState, useRef, useEffect, useMemo,KeyboardEvent } from 'react';
import {
    TextField,
    Button,
    Grid,
    Paper,
    MenuItem,
    Box,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
    Checkbox,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Typography,
    Dialog as MuiDialog,
    DialogTitle as MuiDialogTitle,
    DialogContent as MuiDialogContent,
    Snackbar,
    Alert,
} from '@mui/material';
import {
    Add as AddIcon
} from '@mui/icons-material';
import { createChecklist, createChecklistTemplete, createChecklistUser, deleteChecklist, getAllChecklistCategories, updateChecklist, updateChecklistTemplate } from 'core/API/checklist';
import styles from './ChecklistSetting.module.scss';
import TemplatePage from '../TemplatePage/TemplatePage';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { DraggableRow } from './DraggableRow';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog';

const CHECKLIST_NAME_MAX_LENGTH = 50;

// Define the structure of a checklist item
interface ChecklistItem {
    id: string;
    section: string;
    duty: string;
    instructions: string;
    answerType: string;
    correctAnswer: string;
    attachmentRequired: boolean;
    isEditing: boolean;
    isSaved: boolean;
    isSelected: boolean;
    originalValues?: {
        section: string;
        duty: string;
        instructions: string;
        answerType: string;
        correctAnswer: string;
        attachmentRequired: boolean;
    };
    isValid: {
        section: boolean;
        duty: boolean;
        instructions: boolean;
        answerType: boolean;
        correctAnswer: boolean;
    };
    errors: {
        section?: string;
        duty?: string;
        instructions?: string;
        answerType?: string;
        correctAnswer?: string;
    };
}

interface ValidationRule {
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    pattern?: RegExp;
    patternMessage?: string;
    options?: string[];
    dynamicOptions?: (item: ChecklistItem) => string[];
}

export const AddChecklist: React.FC<{
    onBack: () => void;
    categorysData: { id: number; label: string }[];
    categories: string[];
    frequencyData: { id: number; label: string }[]; // Fix type here
    dataa: any;
    handleTemplateUserChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleSignoffChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleDropdownChange: (e: React.ChangeEvent<{ value: unknown }>) => void;
    handleTemplateChange: (e: React.ChangeEvent<{ value: unknown }>) => void;
    handleFrequencyChange: (e: React.ChangeEvent<{ value: unknown }>) => void;
    handleFormSubmit: () => void;
    headerdata?: any;
    headerMenu?: any;
    editValues?: any;
    refreshChecklistTemplates: () => Promise<void>; // <-- Add this prop
}> = ({
    onBack,
    categorysData,
    categories,
    frequencyData, // Now correctly typed
    dataa,
    handleTemplateUserChange,
    handleSignoffChange,
    handleDropdownChange,
    handleTemplateChange,
    handleFrequencyChange,
    handleFormSubmit,
    headerdata,
    editValues,
    refreshChecklistTemplates // <-- Destructure here
}) => {

        const headerData = {
            version: 1,
            checklistName: 'Safety Inspection',
            category: 'Compliance',
            owner: 'John Doe',
            frequency: 'Weekly',
            date: new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true }) || '07:10 PM IST, Monday, July 14, 2025',
        };

        const columns = [
            { id: 'section', header: 'Section', validation: { required: false, maxLength: 64 } as ValidationRule },
            { id: 'duty', header: 'Duty', validation: { required: true, maxLength: 256 } as ValidationRule },
            { id: 'instructions', header: 'Instructions', validation: { required: false, maxLength: 256 } as ValidationRule },
            {
                id: 'answerType',
                header: 'Answer',
                validation: { required: true, options: ['True/False', 'Checkbox'] } as ValidationRule,
            },
            {
                id: 'correctAnswer',
                header: 'Correct',
                validation: {
                    required: true,
                    dynamicOptions: (item: ChecklistItem) => {
                        if (item.answerType === 'True/False') {
                            return ['True', 'False',];
                        } else if (item.answerType === 'Checkbox') {
                            return ['Checked', 'Unchecked',];
                        }
                        return [];
                    },
                } as ValidationRule,
            },
        ];
        const [items, setItems] = useState<ChecklistItem[]>([
            {
                id: `${Date.now()}${Math.random().toString(36).substring(2, 9)}`,
                section: '',
                duty: '',
                instructions: '',
                answerType: '',
                correctAnswer: '',
                attachmentRequired: false,
                isEditing: true,
                isSaved: false,
                isSelected: false,
                isValid: {
                    section: true,
                    duty: true,
                    instructions: true,
                    answerType: true,
                    correctAnswer: true,
                },
                errors: {},
            }
        ]); 

        const moveRow = (dragIndex: number, hoverIndex: number) => {
            const updatedItems = [...items];
            const [draggedItem] = updatedItems.splice(dragIndex, 1);
            updatedItems.splice(hoverIndex, 0, draggedItem);
            setItems(updatedItems);
        };

        const toggleRowSelection = (id: string) => {
            setItems((prevItems) =>
                prevItems.map((item) => (item.id === id ? { ...item, isSelected: !item.isSelected } : item))
            );
        };
        
        const cellRefs = useRef<{ [key: string]: HTMLInputElement | HTMLTextAreaElement | null }>({});

        const handleCellFocus = (rowIndex: number, colId: string) => {
            setActiveCell({ rowIndex, colId });
        };
        const [activeCell, setActiveCell] = useState<{ rowIndex: number; colId: string } | null>(null);

        const handleKeyDown = (e: KeyboardEvent<any>, rowIndex: number, colId: string) => {
            if (!activeCell) return;
    
            const { key } = e;
            // Allow caret movement in inputs for left/right arrows; only navigate at edges
            if (key === 'ArrowLeft' || key === 'ArrowRight') {
                const target = e.target as HTMLInputElement | HTMLTextAreaElement;
                const isInput = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA');
                if (isInput) {
                    const len = target.value?.length ?? 0;
                    const start = target.selectionStart ?? 0;
                    const end = target.selectionEnd ?? start;
                    const atStart = start === 0 && end === 0;
                    const atEnd = end === len;
                    if ((key === 'ArrowLeft' && !atStart) || (key === 'ArrowRight' && !atEnd)) {
                        return; // let caret move inside field
                    }
                }
            }
            let nextRow = activeCell.rowIndex;
            let nextColIndex = columns.findIndex((col) => col.id === activeCell.colId);
            let nextColId = colId;
    
            const lastColumnIndex = columns.length - 1;
    
            switch (key) {
                case 'ArrowUp':
                    if (nextColIndex === 0) {
                        nextRow = Math.max(0, rowIndex - 1);
                        nextColId = columns[lastColumnIndex].id;
                    } else {
                        nextColIndex = Math.max(0, nextColIndex - 1);
                        nextColId = columns[nextColIndex].id;
                    }
                    e.preventDefault();
                    break;
                case 'ArrowDown':
                    if (rowIndex < items.length - 1) {
                        nextRow = rowIndex + 1;
                        nextColId = columns[0].id;
                    } else if (rowIndex === items.length - 1 && items.length < 256) {
                        addSingleRow();
                        nextRow = items.length;
                        nextColId = columns[0].id;
                    }
                    e.preventDefault();
                    break;
                case 'ArrowLeft':
                    nextColIndex = Math.max(0, nextColIndex - 1);
                    nextColId = columns[nextColIndex].id;
                    e.preventDefault();
                    break;
                case 'ArrowRight':
                    nextColIndex = Math.min(lastColumnIndex, nextColIndex + 1);
                    nextColId = columns[nextColIndex].id;
                    e.preventDefault();
                    break;
                case 'Tab':
                    if (e.shiftKey) {
                        if (nextColIndex > 0) {
                            nextColIndex -= 1;
                            nextColId = columns[nextColIndex].id;
                        } else if (rowIndex > 0) {
                            nextRow = rowIndex - 1;
                            nextColId = columns[lastColumnIndex].id;
                        }
                    } else {
                        if (nextColIndex < lastColumnIndex) {
                            nextColIndex += 1;
                            nextColId = columns[nextColIndex].id;
                        } else if (rowIndex < items.length - 1) {
                            nextRow = rowIndex + 1;
                            nextColIndex = 0;
                            nextColId = columns[0].id;
                        } else if (items.length < 256) {
                            addSingleRow();
                            nextRow = items.length;
                            nextColIndex = 0;
                            nextColId = columns[0].id;
                        }
                    }
                    e.preventDefault();
                    break;
                case 'Enter':
                    if (rowIndex < items.length - 1) {
                        nextRow = rowIndex + 1;
                        nextColId = columns[0].id;
                    } else if (rowIndex === items.length - 1 && items.length < 256) {
                        addSingleRow();
                        nextRow = items.length;
                        nextColId = columns[0].id;
                    }
                    e.preventDefault();
                    break;
                default:
                    return;
            }
    
            if (nextRow !== rowIndex || nextColId !== colId) {
                setTimeout(() => {
                    const cellRefKey = `${nextRow}-${nextColId}`;
                    const nextCellRef = cellRefs.current[cellRefKey];
                    if (nextCellRef) nextCellRef.focus();
                    setActiveCell({ rowIndex: nextRow, colId: nextColId });
                }, 0);
            }
        };

        const createEmptyRow = (): ChecklistItem => ({
            id: `${Date.now()}${Math.random().toString(36).substring(2, 9)}`,
            section: '',
            duty: '',
            instructions: '',
            answerType: '',
            correctAnswer: '',
            attachmentRequired: false,
            isEditing: true,
            isSaved: false,
            isSelected: false,
            isValid: {
                section: true,
                duty: true,
                instructions: true,
                answerType: true,
                correctAnswer: true,
            },
            errors: {},
        });

        const addSingleRow = () => {
            const newRow = createEmptyRow();
            setItems((prevItems) => [...prevItems, newRow]);
            
            // Focus on the Section field of the newly added row after a short delay
            setTimeout(() => {
                const newRowIndex = items.length; // The index of the newly added row
                const sectionFieldRef = cellRefs.current[`${newRowIndex}-section`];
                if (sectionFieldRef) {
                    sectionFieldRef.focus();
                }
            }, 100); // Small delay to ensure the DOM has updated
        };

        const validateCell = (
            value: string,
            columnId: string,
            item: ChecklistItem
        ): { valid: boolean; message?: string } => {
            const column = columns.find((col) => col.id === columnId);
            if (!column || !column.validation) return { valid: true };
    
            const { validation } = column;
    
            if (validation.required && !value.trim()) return { valid: false, message: 'This field is required' };
            if (validation.minLength && value.trim().length < validation.minLength)
                return { valid: false, message: `Must be at least ${validation.minLength} characters` };
            if (validation.maxLength && value.trim().length > validation.maxLength)
                return { valid: false, message: `Must be no more than ${validation.maxLength} characters` };
            if (validation.pattern && !validation.pattern.test(value))
                return { valid: false, message: validation.patternMessage || 'Invalid format' };
            if (validation.options && value.trim() && !validation.options.includes(value.trim()))
                return { valid: false, message: `Must be one of: ${validation.options.join(', ')}` };
            if (validation.dynamicOptions) {
                const validOptions = validation.dynamicOptions(item);
                if (value.trim() && !validOptions.includes(value.trim())) {
                    return { valid: false, message: `Must be one of: ${validOptions.join(', ')}` };
                }
            }
    
            return { valid: true };
        };
    

        const updateField = (id: string, field: string, value: string) => {
            setItems((prevItems) =>
                prevItems.map((item) => {
                    if (item.id !== id) return item;
                    const validation = validateCell(value, field, item);
                    const updatedItem = {
                        ...item,
                        [field]: value,
                        isValid: { ...item.isValid, [field]: validation.valid },
                        errors: { ...item.errors, [field]: validation.valid ? undefined : validation.message },
                    };
                    if (field === 'answerType') {
                        // Set default correctAnswer based on answerType
                        if (value === 'True/False') {
                            updatedItem.correctAnswer = 'True';
                        } else if (value === 'Checkbox') {
                            updatedItem.correctAnswer = 'Checked';
                        } else {
                            updatedItem.correctAnswer = '';
                        }
                        updatedItem.isValid.correctAnswer = true;
                        updatedItem.errors.correctAnswer = undefined;
                        const correctAnswerValidation = validateCell(updatedItem.correctAnswer, 'correctAnswer', updatedItem);
                        updatedItem.isValid.correctAnswer = correctAnswerValidation.valid;
                        updatedItem.errors.correctAnswer = correctAnswerValidation.valid
                            ? undefined
                            : correctAnswerValidation.message;
                    }
                    return updatedItem;
                })
            );
        };

        const handleCancelClick = (id: string) => {
            cancelEdit(id);
        };

        const updateAttachmentRequired = (id: string, value: boolean) => {
            setItems((prevItems) =>
                prevItems.map((item) =>
                    item.id === id ? { ...item, attachmentRequired: value } : item
                )
            );
        };

        const toggleEditMode = useRef((id: string) => {
            setItems((prevItems) =>
                prevItems.map((item) => {
                    if (item.id === id) {
                        const isEditing = !item.isEditing;
                        if (isEditing) {
                            // Store original values when entering edit mode
                            const originalValues = {
                                section: item.section,
                                duty: item.duty,
                                instructions: item.instructions,
                                answerType: item.answerType,
                                correctAnswer: item.correctAnswer,
                                attachmentRequired: item.attachmentRequired,
                            };
                            const { updatedItem } = validateRow(item, false);
                            return { ...updatedItem, isEditing, originalValues };
                        } else {
                            if (!item.isSaved) {
                                const savedItem = {
                                    ...item,
                                    isEditing: false,
                                    isSaved: true, // Mark as saved when exiting edit mode
                                    errors: {},
                                    isValid: {
                                        section: true,
                                        duty: true,
                                        instructions: true,
                                        answerType: true,
                                        correctAnswer: true,
                                    },
                                };
                                
                                // Clear duties error if we now have valid items
                                const newItems = prevItems.map((i) => (i.id === id ? savedItem : i));
                                const hasValidItems = newItems.some(i => 
                                    i.isSaved || 
                                    (i.duty && i.answerType && i.correctAnswer)
                                );
                                if (hasValidItems && formErrors.duties.error) {
                                    setFormErrors(prev => ({
                                        ...prev,
                                        duties: { error: false, helperText: '' }
                                    }));
                                }
                                
                                return savedItem;
                            }
                            return { ...item, isEditing: false };
                        }
                    }
                    return item;
                })
            );
        }).current;

        const deleteRow = (id: string) => {
            setItems((prevItems) => {
                const newItems = prevItems.filter((item) => item.id !== id);
                
                // Check if we still have valid items after deletion
                const hasValidItems = newItems.some(item => 
                    item.isSaved || 
                    (item.duty && item.answerType && item.correctAnswer)
                );
                if (!hasValidItems && !formErrors.duties.error) {
                    setFormErrors(prev => ({
                        ...prev,
                        duties: { error: true, helperText: 'At least one duty must be added to create a checklist template' }
                    }));
                }
                
                return newItems;
            });
        };

        const validateRow = (
            item: ChecklistItem,
            updateItem: boolean = true
        ): { valid: boolean; updatedItem: ChecklistItem } => {
            let allValid = true;
            const updatedItem = { ...item, errors: { ...item.errors }, isValid: { ...item.isValid } };
    
            ['section', 'duty', 'instructions', 'answerType', 'correctAnswer'].forEach((field) => {
                const validation = validateCell(item[field as keyof ChecklistItem] as string, field, item);
                updatedItem.isValid[field as keyof typeof item.isValid] = validation.valid;
                updatedItem.errors[field as keyof typeof item.errors] = validation.valid ? undefined : validation.message;
                if (!validation.valid) {
                    allValid = false;
                }
            });
    
            if (updateItem) {
                setItems((prevItems) =>
                    prevItems.map((i) => (i.id === item.id ? updatedItem : i))
                );
            }
    
            return { valid: allValid, updatedItem };
        };
    
        const isRowLimitReached = items.length >= 256;


        const cancelEdit = (id: string) => {
            const item = items.find((item) => item.id === id);
            if (!item) return;
            
            setItems((prevItems) =>
                prevItems.map((item) => {
                    if (item.id === id) {
                        if (item.isSaved && item.originalValues) {
                            // Restore original values for saved items
                            return {
                                ...item,
                                isEditing: false,
                                section: item.originalValues.section,
                                duty: item.originalValues.duty,
                                instructions: item.originalValues.instructions,
                                answerType: item.originalValues.answerType,
                                correctAnswer: item.originalValues.correctAnswer,
                                attachmentRequired: item.originalValues.attachmentRequired,
                                errors: {},
                                isValid: {
                                    section: true,
                                    duty: true,
                                    instructions: true,
                                    answerType: true,
                                    correctAnswer: true,
                                },
                                originalValues: undefined, // Clear original values
                            };
                        } else if (!item.isSaved) {
                            // Delete unsaved items
                            return null;
                        } else {
                            // Just exit edit mode if no original values
                            return { ...item, isEditing: false };
                        }
                    }
                    return item;
                }).filter(Boolean) // Remove null items
            );
        };

        const validateAllCells = (): { isValid: boolean; invalidRowIndices: number[] } => {
            let allValid = true;
            const invalidRowIndices: number[] = [];
    
            const newItems = items.map((item, index) => {
                // Only validate rows that have content or are saved
                const shouldValidate = item.isSaved || 
                    (item.section || item.duty || item.instructions || item.answerType || item.correctAnswer);
    
                if (!shouldValidate) {
                    return {
                        ...item,
                        errors: {},
                        isValid: {
                            section: true,
                            duty: true,
                            instructions: true,
                            answerType: true,
                            correctAnswer: true,
                        },
                    };
                }
    
                const { valid, updatedItem } = validateRow(item, false);
                if (!valid) {
                    allValid = false;
                    invalidRowIndices.push(index);
                }
                return updatedItem;
            });
    
            setItems(newItems);
            return { isValid: allValid, invalidRowIndices };
        };

        const [snackbar, setSnackbar] = useState<{
            open: boolean;
            message: string;
            severity: 'success' | 'error' | 'info' | 'warning';
        }>({
            open: false,
            message: '',
            severity: 'info',
        });

        const workplacesCtx = useWorkplacesContext();
        const getDataAsJson = () => {
            // Get workplace ID from context (current workplace) - always use current workplace ID
            const currentWorkplaceId = workplacesCtx?.workplaceId || parseInt(localStorage.getItem("workplace-id") || '1');
            const workplaceId = currentWorkplaceId.toString();
            // Get current user ID from localStorage
            const currentUserId = localStorage.getItem("userId") || "1";
            // Get organization ID dynamically from useWorkplaceUser hook data (passed as prop)
            const organizationId = (dataa?.userOrg?.id || '1').toString();
            // Determine scope: Organization vs Workplace
            const isOrganization = (formValues.templateUser || '').toLowerCase() === 'organization';
            // Always include both fields; backend decides which to use. Maintain dynamic type.
            const type = isOrganization ? 'organization' : 'workplace';
            const workplaceIri = `api/workplaces/${workplaceId}`;
            const organizationIri = `api/organizations/${organizationId}`;
            
            // Create the payload with the required structure for the API
            return {
                workplace: workplaceIri,
                organization: organizationIri,
                type,
                category: `api/checklist_categories/${formValues.dropdownValue || ''}`,
                frequency: `api/checklist_frequencies/${formValues.frequencyValue || ''}`,
                label: formValues.templateValue, // Fixed: using templateValue for label
                signoffRequired: formValues.signoffRequired,
                templateOwner: `api/users/${currentUserId}`, // Use dynamic user ID from localStorage
                author: `api/users/${currentUserId}`, // Add author field with dynamic user ID
                tasks: items.filter(item => item.duty || item.answerType || item.correctAnswer)
                .map((item, index) => {
                    // Use numeric API id if present (from edit mode), otherwise undefined
                    let apiId = undefined;
                    if (item.id && item.id.startsWith('task_')) {
                        const parts = item.id.split('_');
                        if (parts.length > 1 && !isNaN(Number(parts[1]))) {
                            apiId = Number(parts[1]);
                        }
                    } else if (!isNaN(Number(item.id))) {
                        apiId = Number(item.id);
                    }
                    return {
                        id: apiId,
                        parent: "",
                        section: item.section || '',
                        duty: item.duty || '',
                        instructions: item.instructions || '',
                        answerType: item.answerType || '',
                        expectedAnswer: item.correctAnswer || '',
                        listOrder: index + 1,
                        attachmentRequired: item.attachmentRequired ?? false,
                    };
                }),
            };
        };


        const submitForm = async () => {
            // Always run both validations
            const { isValid: rowsValid, invalidRowIndices } = validateAllCells();
            const formValid = validate();

            if (rowsValid && formValid) {
                try {
                    const payload = getDataAsJson();
                    if (editValues) {
                        await updateChecklistTemplate(editValues.id, payload);
                        setSnackbar({
                            open: true,
                            message: 'Checklist template updated successfully',
                            severity: 'success',
                        });
                    } else {
                        await createChecklistTemplete(payload);
                        setSnackbar({
                            open: true,
                            message: 'Checklist template created successfully',
                            severity: 'success',
                        });
                    }
                    await refreshChecklistTemplates();
                    onBack();
                } catch (error) {
                    const errorMessage = editValues 
                        ? 'Failed to update checklist template'
                        : 'Failed to create checklist template';
                    setSnackbar({
                        open: true,
                        message: errorMessage,
                        severity: 'error',
                    });
                }
            } else {
                let msg = '';
                if (!formValid) {
                    msg += 'Please fill all required fields. ';
                }
                if (!rowsValid) {
                    const rowNumbers = invalidRowIndices.map((index) => index + 1).join(', ');
                    msg += `Please fix validation errors in row${invalidRowIndices.length > 1 ? 's' : ''} ${rowNumbers} before submitting.`;
                }
                setSnackbar({
                    open: true,
                    message: msg.trim(),
                    severity: 'error',
                });
            }
        }
        const defaultWorkplaceId = workplacesCtx?.workplaceId || parseInt(localStorage.getItem('workplace-id') || '1');
        const [formValues, setFormValues] = useState({
            templateValue: '',
            dropdownValue: '',
            frequencyValue: '',
            templateUser: 'Workplace',
            signoffRequired: false,
            workplaceId: defaultWorkplaceId,
          });

        
          // Single state object for errors and helper texts
        const [formErrors, setFormErrors] = useState({
            templateValue: { error: false, helperText: '' },
            dropdownValue: { error: false, helperText: '' },
            frequencyValue: { error: false, helperText: '' },
            templateUser: { error: false, helperText: '' }, // Add error state for Checklist User
            signoffRequired: { error: false, helperText: '' },
            duties: { error: false, helperText: '' }, // Add error state for duties validation
        });

        const validate = () => {
            let valid = true;
            const newErrors = {
              templateValue: { error: false, helperText: '' },
              dropdownValue: { error: false, helperText: '' },
              frequencyValue: { error: false, helperText: '' },
              templateUser: { error: false, helperText: '' },
              signoffRequired: { error: false, helperText: '' },
              duties: { error: false, helperText: '' },
            };
        
            if (!formValues.templateValue.trim()) {
              newErrors.templateValue = { error: true, helperText: 'Checklist Name is required' };
              valid = false;
            } else if (formValues.templateValue.length > CHECKLIST_NAME_MAX_LENGTH) {
              newErrors.templateValue = { error: true, helperText: `Maximum ${CHECKLIST_NAME_MAX_LENGTH} characters allowed` };
              valid = false;
            }
            if (!formValues.dropdownValue) {
              newErrors.dropdownValue = { error: true, helperText: 'Please select a checklist category' };
              valid = false;
            }
            if (!formValues.frequencyValue) {
              newErrors.frequencyValue = { error: true, helperText: 'Please select a frequency' };
              valid = false;
            }
            if (!formValues.templateUser) {
              newErrors.templateUser = { error: true, helperText: 'Please select Workplace or Organization' };
              valid = false;
            }

            // Check if at least one duty is added (either saved or has content)
            const validItems = items.filter(item => 
                item.isSaved || 
                (item.duty && item.answerType && item.correctAnswer)
            );
            if (validItems.length === 0) {
              // Add a general error for duties
              newErrors.duties = { error: true, helperText: 'At least one duty must be added to create a checklist template' };
              valid = false;
            }
        
            setFormErrors(newErrors);
            return valid;
          };

        const handleChange = (field) => (event) => {
            const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
            setFormValues((prev) => ({
            ...prev,
            [field]: value,
            }));

            // Clear individual field error as user corrects input
            setFormErrors((prev) => ({
                ...prev,
                [field]: { error: false, helperText: '' },
            }));
        };

        useEffect(() => {
            if (editValues) {
                const typeVal = (editValues?.type || '').toString().toLowerCase();
                const scope = typeVal === 'organization' ? 'Organization' : typeVal === 'workplace' ? 'Workplace' : 'Workplace';
                setFormValues((prev) => ({
                    ...prev,
                    signoffRequired: editValues?.signoff_required || false,
                    frequencyValue: (editValues?.frequency?.id || '').toString(),
                    dropdownValue: (editValues?.category?.id || '').toString(),
                    templateValue: editValues?.label || '',
                    templateUser: scope,
                    workplaceId: editValues?.workplace?.id || prev.workplaceId,
                }));

                const formattedTasks = editValues?.tasks?.map(task => ({
                    id: `task_${task?.id || Date.now()}${Math.random().toString(36).substring(2, 9)}`,
                    section: task?.section || '',
                    duty: task?.duty || '',
                    instructions: task?.instructions || '',
                    answerType: task?.answerType || '', // Match API response property
                    correctAnswer: task?.expectedAnswer || '', // Match API response property
                    attachmentRequired: task?.attachmentRequired ?? false,
                    isEditing: false,
                    isSaved: true,
                    isSelected: false,
                    isValid: {
                        section: true,
                        duty: true,
                        instructions: true,
                        answerType: true,
                        correctAnswer: true,
                    },
                    errors: {},
                })) || [];
                
                // If there are no tasks or tasks array is empty, add an empty row
                if (!formattedTasks.length) {
                    setItems([createEmptyRow()]);
                } else {
                    setItems(formattedTasks);
                }
            } else {
                // Add mode initialization - default to Workplace but allow user to change
                setFormValues((prev) => ({ ...prev, templateUser: 'Workplace' }));
            }
        }, [editValues?.id, editValues?.type]); 

        return (
            <Paper elevation={3} className={styles.containerr} style={{ padding: '20px', backgroundColor: 'whitesmoke' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Button variant="outlined" onClick={onBack} className={styles.button}>
                        Back
                    </Button>
                    <Button
                        style={{
                            backgroundColor: 'rgb(68, 99, 136)',
                            color: 'white',
                            padding: '5px 15px',
                            textTransform: 'capitalize',
                        }}
                        
                        onClick={submitForm}
                        >
                        Save
                    </Button>
                </Box>

                <div className={styles.rightPanel}>
                    <Box display="flex" flexDirection="row" alignItems="end" gap={8}>
                        <FormControl component="fieldset" error={formErrors.templateUser.error}>
                            <FormLabel component="legend">Checklist User</FormLabel>
                            <RadioGroup row value={formValues.templateUser} onChange={handleChange('templateUser')}>
                                <FormControlLabel
                                    value="Workplace"
                                    control={<Radio color="primary" />}
                                    label="Workplace"
                                />
                                <FormControlLabel
                                    value="Organization"
                                    control={<Radio color="primary" />}
                                    label="Organization"
                                />
                            </RadioGroup>
                            {formErrors.templateUser.error && (
                                <Typography color="error" variant="caption">{formErrors.templateUser.helperText}</Typography>
                            )}
                        </FormControl>
                        <FormControl component="fieldset" error={formErrors.signoffRequired.error}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formValues.signoffRequired}
                                        onChange={handleChange('signoffRequired')}
                                        color="primary"
                                    />
                                }
                                label="Signoff Required"
                            />
                            {formErrors.signoffRequired.error && (
                                <Typography color="error" variant="caption">{formErrors.signoffRequired.helperText}</Typography>
                            )}
                        </FormControl>
                    </Box>
                    <Box mt={2}>
                        <TextField
                            select
                            label="Select Checklist Category"
                            value={formValues.dropdownValue}
                            onChange={handleChange('dropdownValue')}
                            fullWidth
                            required
                            error={formErrors.dropdownValue.error}
                            helperText={formErrors.dropdownValue.helperText}
                        >
                            <MenuItem value="">-- Select Option --</MenuItem>
                            {categorysData.length !== 0 ? (
                                categorysData.map((category) => (
                                    <MenuItem value={(category?.id || '').toString()} key={category?.id || Math.random()}>
                                        {category?.label || 'Unnamed Category'}
                                    </MenuItem>
                                ))
                            ) : (
                                <MenuItem disabled>No data found</MenuItem>
                            )}
                        </TextField>
                    </Box>
                    <Box mt={2}>
                        <TextField
                            label="Checklist Name"
                            value={formValues.templateValue}
                            onChange={handleChange('templateValue')}
                            fullWidth
                            required
                            error={formErrors.templateValue.error}
                            helperText={
                                formErrors.templateValue.helperText ||
                                `${formValues.templateValue.length}/${CHECKLIST_NAME_MAX_LENGTH}`
                            }
                            inputProps={{ maxLength: CHECKLIST_NAME_MAX_LENGTH }}
                        />
                    </Box>
                    <Box mt={2}>
                        <TextField
                            select
                            label="Frequency"
                            value={(formValues.frequencyValue || '').toString()}
                            onChange={handleChange('frequencyValue')}
                            fullWidth
                            required
                            error={formErrors.frequencyValue.error}
                            helperText={formErrors.frequencyValue.helperText}
                        >
                            <MenuItem value="">-- Select Frequency --</MenuItem>
                            {Array.isArray(frequencyData) && frequencyData.length > 0 ? (
                                frequencyData.map((data) => (
                                    <MenuItem value={(data?.id || '').toString()} key={data?.id || Math.random()}>
                                        {data?.label || 'Unnamed Frequency'}
                                    </MenuItem>
                                ))
                            ) : (
                                <MenuItem disabled>No data found</MenuItem>
                            )}
                        </TextField>
                    </Box>

                    <TableContainer className={styles.consistentTableContainer} style={{ maxHeight: 'calc(100vh - 250px)', marginTop: '20px' }}>
                        <Table className={styles.consistentTable} stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox"></TableCell>
                                    {columns
                                        .filter((column) => !['answerType', 'correctAnswer'].includes(column.id) || (!headerdata || headerdata === ''))
                                        .map((column) => (
                                            <TableCell key={column.id} sx={{ p: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    {column.header}
                                                    {column.validation?.required && (
                                                        <Typography component="span" color="error" sx={{ ml: 0.5 }}>
                                                            *
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        ))}
                                    {(!headerdata || headerdata === '') && (
                                        <TableCell sx={{ whiteSpace: 'nowrap', width: '80px', textAlign: 'center' }}>Picture Required</TableCell>
                                    )}
                                    {(!headerdata || headerdata === '') && (
                                        <TableCell>Actions</TableCell>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((item, index) => (
                                    <DraggableRow
                                        headerdata={headerdata}
                                        key={item.id}
                                        item={item}
                                        index={index}
                                        moveRow={moveRow}
                                        toggleRowSelection={toggleRowSelection}
                                        columns={columns}
                                        cellRefs={cellRefs}
                                        handleCellFocus={handleCellFocus}
                                        handleKeyDown={handleKeyDown}
                                        updateField={updateField}
                                        updateAttachmentRequired={updateAttachmentRequired}
                                        toggleEditMode={toggleEditMode}
                                        deleteRow={deleteRow}
                                        handleCancelClick={handleCancelClick}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                        {items.length === 0 && (
                            <Box sx={{ p: 4, textAlign: 'center' }}>
                                <Typography variant="body1" color="text.secondary">
                                    No items added yet. Click to create your first checklist item.
                                </Typography>
                                
                                {/* Display duties validation error below the "no items" message */}
                                {formErrors.duties.error && (
                                    <Box sx={{ mt: 2, textAlign: 'left' }}>
                                        <Typography variant="body2" color="error">
                                            {formErrors.duties.helperText}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}

                    <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                            {(!headerdata || headerdata === '') && (
                                                <Button
                                                    style={{
                                                        backgroundColor: isRowLimitReached ? 'gray' : 'rgb(68, 99, 136)',
                                                        color: 'white',
                                                        padding: '5px 15px',
                                                        fontSize: '12px',
                                                    }}
                                                    startIcon={<AddIcon />}
                                                    onClick={addSingleRow}
                                                    disabled={isRowLimitReached}
                                                >
                                                    Add Row
                                                </Button>
                                            )}
                                           
                                        </Box>
                                        {(!headerdata || headerdata === '') && (
                                            <Typography variant="caption" color="text.secondary">
                                                Pro tip: Use Tab, Enter, and arrow keys to navigate between cells. Fields marked with * are required.
                                            </Typography>
                                        )}
                                    </Box>
                        
                    {/* <TemplatePage onBackClick={onBack} /> */}
                </div>       

                
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                        severity={snackbar.severity}
                        variant="filled"
                        className={styles.snackbar}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Paper>
        );
    };